/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Check, AlertTriangle, FileText, Loader2, Pause, Play } from 'lucide-react';

/*
  Auto-playing "AI in action" panel. Cycles through three real MPL
  Infrastructure Grant claims (Bhopal → Sagar → Chambal), replaying the
  actual 5-step AI screening output for each — verbatim rationale, gap
  detection, and inline coaching. Each claim runs ~30s; then the loop
  restarts. Pure React state + setInterval, no external assets.
*/

const CLAIMS = [
  {
    id: 'bhopal',
    division: 'Bhopal Division',
    grant: 'MPL Infrastructure Development Grant',
    docsProcessed: 12,
    aiTime: '47 sec',
    steps: [
      {
        n: 1,
        label: 'Proposal · Nature & description',
        file: 'Proposal.pdf',
        verdict: 'flag',
        ai: 'No page content was visible for this attachment, so the document type and required proposal details cannot be verified.',
        coach: 'Re-upload the actual proposal PDF showing the infrastructure description, justification, cost breakup, timeline, and resolution pages.',
      },
      {
        n: 2,
        label: 'Justification & expected benefit',
        file: 'MPL covering letter.pdf',
        verdict: 'flag',
        ai: 'No pages were available to inspect for this attachment, so its actual document type cannot be verified.',
        coach: 'Re-upload the actual scanned pages so the infrastructure justification can be reviewed.',
      },
      {
        n: 3,
        label: 'Vendor quotation authenticity',
        file: 'Net estimate.pdf',
        verdict: 'flag',
        ai: 'This scan appears to be a vendor estimate/quotation for net-related work, showing pricing rather than a proposal document. It does not match the tagged slot.',
        coach: 'Re-tag this file under the correct quotation slot, or upload the required document for this slot.',
      },
      {
        n: 4,
        label: 'Proposed timeline',
        file: 'Proposal for Utilization.pdf',
        verdict: 'flag',
        ai: 'Only identified as a proposal for utilization — no specific week-wise, month-wise, or milestone-based completion timeline visible.',
        coach: 'Re-upload a clear scan showing the actual proposed completion timeline with specific dates or milestones.',
      },
      {
        n: 5,
        label: 'Committee resolution',
        file: 'RESOLUTION.pdf',
        verdict: 'pass',
        ai: 'Committee resolution document identified. Scan shows the required approval detail. Signatures visible on the resolution page.',
        coach: null,
      },
    ],
  },
  {
    id: 'sagar',
    division: 'Sagar Division',
    grant: 'MPL Infrastructure Development Grant',
    docsProcessed: 11,
    aiTime: '52 sec',
    steps: [
      {
        n: 1,
        label: 'Proposal · Nature & description',
        file: 'Sagar_Ground_Proposal.pdf',
        verdict: 'flag',
        ai: 'Proposal covers outfield levelling and boundary-rope replacement, but the "roller servicing" line item carries a fixed lump-sum figure with no unit rate, no vendor named, and no scope narrative. It reads like a placeholder rather than a costed item.',
        coach: 'Add a vendor name, unit rate, and 2–3 line scope for the roller-servicing item, or drop it from the proposal.',
      },
      {
        n: 2,
        label: 'Justification & expected benefit',
        file: 'Sagar_Covering_Letter.pdf',
        verdict: 'flag',
        ai: 'Letter states the benefit as "improving playing conditions." That is generic — the reviewer needs a specific outcome (players benefited, additional matches, category e.g. U-16 / senior).',
        coach: 'Rewrite the benefit paragraph as "Enables N additional home-ground U-16 matches in the Sept–Nov window, benefiting ~120 players." Re-upload.',
      },
      {
        n: 3,
        label: 'Vendor quotation authenticity',
        file: 'Quotation_Turf.pdf',
        verdict: 'flag',
        ai: 'GSTIN printed (07AABCT…) is a Delhi state code, not Madhya Pradesh (23). PAN block is missing from the footer entirely. The quotation grand total also does not reconcile with the sum of its own line items — arithmetic gap on page 1.',
        coach: 'Ask the vendor to re-issue with the correct MP GSTIN, a visible PAN, and a total that matches the line-item sum. Re-upload after correction.',
      },
      {
        n: 4,
        label: 'Proposed timeline',
        file: 'Sagar_Timeline.pdf',
        verdict: 'flag',
        ai: 'Timeline lists week-1 to week-6 milestones, but week-4 ("re-turfing") overlaps with a scheduled U-19 fixture on the same ground on 12-Sep. The drying window also runs into monsoon dates.',
        coach: 'Re-plan the turfing window to end at least 7 days before 12-Sep, and shift the drying window out of the rain forecast band.',
      },
      {
        n: 5,
        label: 'Committee resolution',
        file: 'Sagar_Resolution.pdf',
        verdict: 'pass',
        ai: 'Committee resolution dated 14-Aug identifies the sanctioned amount, purpose, and authorises the secretary to draw funds. Chairman and secretary signatures visible on the resolution page.',
        coach: null,
      },
    ],
  },
  {
    id: 'chambal',
    division: 'Chambal Division',
    grant: 'MPL Infrastructure Development Grant',
    docsProcessed: 10,
    aiTime: '41 sec',
    steps: [
      {
        n: 1,
        label: 'Proposal · Nature & description',
        file: 'Chambal_Proposal.pdf',
        verdict: 'pass',
        ai: 'Proposal identifies dressing-room refurbishment and floodlight servicing. Scope, cost heads, and justification are itemised across 4 pages with clear per-item breakup.',
        coach: null,
      },
      {
        n: 2,
        label: 'Justification & expected benefit',
        file: 'Chambal_Justification.pdf',
        verdict: 'flag',
        ai: 'Justification quantifies the benefit (hosting 6 additional inter-district matches) but the claimed savings figure on venue-hire is not backed by any invoice or reference — it appears asserted rather than sourced.',
        coach: 'Attach 1–2 prior venue-hire invoices or a signed rate card so the claimed saving can be verified.',
      },
      {
        n: 3,
        label: 'Vendor quotation authenticity',
        file: 'Electrical_Quote.pdf',
        verdict: 'flag',
        ai: 'Letterhead font and logo alignment shift between page 1 and page 2, suggesting the pages may not be from the same original document. The "signed date" also appears rubber-stamped over what looks like an earlier, whited-out date.',
        coach: 'Re-request a fresh, single-source PDF quotation on the vendor letterhead with a clean signature and date. Re-upload.',
      },
      {
        n: 4,
        label: 'Proposed timeline',
        file: 'Chambal_Schedule.pdf',
        verdict: 'flag',
        ai: 'Timeline says "completion within 45 days" but does not list milestone-wise dates. The end date also falls 2 days after the first scheduled home fixture on 08-Sep. Floodlight commissioning window is unrealistic given the 6-day electrical safety inspection lag.',
        coach: 'Share a milestone-wise schedule (dates for procurement, installation, safety inspection, commissioning) landing at least 7 days before 08-Sep.',
      },
      {
        n: 5,
        label: 'Committee resolution',
        file: 'Chambal_Resolution.pdf',
        verdict: 'flag',
        ai: 'Resolution dated 22-Aug carries only the secretary\'s signature — the Chairman\'s signature block is present but blank. Corporate-resolution structure requires both signatories for the authorising clause to be valid.',
        coach: 'Countersign the resolution with the Chairman before re-upload. The authorising clause is invalid without both signatories.',
      },
    ],
  },
];

const STEP_MS = 5000; // 5s per step
const OUTRO_MS = 3500; // outro card
const CLAIM_MS = 5 * STEP_MS + OUTRO_MS; // ~28.5s per claim
const TICK_MS = 100; // resolution of the animation

export default function AiScreeningPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setElapsed((e) => e + TICK_MS), TICK_MS);
    return () => clearInterval(id);
  }, [paused]);

  const { claim, claimIdx, inClaim } = useMemo(() => {
    const idx = Math.floor(elapsed / CLAIM_MS) % CLAIMS.length;
    return { claim: CLAIMS[idx], claimIdx: idx, inClaim: elapsed % CLAIM_MS };
  }, [elapsed]);

  const activeIdx = Math.min(claim.steps.length - 1, Math.floor(inClaim / STEP_MS));
  const stepProgress = ((inClaim % STEP_MS) / STEP_MS) * 100;
  const outro = inClaim >= claim.steps.length * STEP_MS;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-orange-400/30 bg-gradient-to-b from-slate-900 to-slate-950 shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-white/5 bg-slate-950/60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-400/30 grid place-items-center">
            <Sparkles className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <div className="text-white text-lg font-semibold" data-testid="ai-panel-claim-title">
              AI in action · {claim.division} claim
            </div>
            <div className="text-sm text-slate-500 mt-0.5">Actual AI screening output · {claim.grant}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2">
            {CLAIMS.map((c, i) => (
              <span
                key={c.id}
                className={`h-2 rounded-full transition-all ${
                  i === claimIdx ? 'w-8 bg-orange-400' : 'w-2 bg-white/15'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            data-testid="ai-panel-pause-toggle"
            className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full border transition ${
              paused
                ? 'text-amber-200 border-amber-400/50 bg-amber-500/15 hover:bg-amber-500/25'
                : 'text-orange-300 border-orange-400/40 bg-orange-500/10 hover:bg-orange-500/20'
            }`}
            title={paused ? 'Resume auto-cycle' : 'Pause auto-cycle'}
          >
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {paused ? 'Paused · resume' : 'Live · pause'}
          </button>
        </div>
      </div>

      {/* Overall step progress bar */}
      <div className="flex gap-1.5 px-8 pt-5">
        {claim.steps.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-emerald-400 transition-all duration-100"
              style={{
                width: i < activeIdx ? '100%' : i === activeIdx ? `${stepProgress}%` : '0%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Body */}
      <div className="px-8 py-8 min-h-[440px]">
        {!outro ? (
          <StepView
            key={`${claim.id}-${activeIdx}`}
            step={claim.steps[activeIdx]}
            idx={activeIdx}
            total={claim.steps.length}
          />
        ) : (
          <Outro key={`${claim.id}-outro`} claim={claim} />
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-4 border-t border-white/5 bg-slate-950/40 flex items-center justify-between text-sm text-slate-500">
        <div>
          Claim {claimIdx + 1} of {CLAIMS.length} · loops every {(CLAIM_MS / 1000).toFixed(0)}s per claim · pure React
        </div>
        <div className="text-slate-600">MPL 2026 · AI-first cricket</div>
      </div>
    </div>
  );
}

function StepView({ step, idx, total }) {
  const passed = step.verdict === 'pass';
  return (
    <div className="animate-[fadeIn_0.4s_ease-out_both]">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes typeIn { from { max-height: 0; opacity: 0; } to { max-height: 200px; opacity: 1; } }
        .typing { animation: typeIn 1.2s ease-out both; overflow: hidden; }
      `}</style>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-10 h-10 rounded-lg bg-orange-500/15 border border-orange-400/30 grid place-items-center text-orange-300 font-bold text-base tabular-nums">
          {String(step.n).padStart(2, '0')}
        </div>
        <div className="text-2xl text-white font-semibold">{step.label}</div>
        <div className="ml-auto flex items-center gap-2 text-sm text-slate-400">
          <FileText className="w-4 h-4" />
          <span className="font-mono">{step.file}</span>
        </div>
      </div>

      {/* AI thinking then verdict */}
      <div className="grid gap-5">
        <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/[0.03] p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-300 mb-3 font-semibold">
            <Loader2 className="w-4 h-4 animate-spin" style={{ animationDuration: '2s' }} />
            AI reasoning
          </div>
          <div className="typing text-lg text-slate-200 leading-relaxed">{step.ai}</div>
        </div>

        {step.coach && (
          <div
            className="rounded-lg border border-amber-400/25 bg-amber-500/[0.04] p-4 typing"
            style={{ animationDelay: '1.4s' }}
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 mb-2">
              <AlertTriangle className="w-3 h-3" />
              Inline coaching for the division
            </div>
            <div className="text-base text-slate-100 leading-relaxed">{step.coach}</div>
          </div>
        )}

        <div className="flex items-center gap-3 typing" style={{ animationDelay: '2.5s' }}>
          {passed ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-sm font-semibold">
              <Check className="w-4 h-4" strokeWidth={3} /> PASSED
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4" /> FLAGGED · needs re-upload
            </div>
          )}
          <div className="text-xs text-slate-500 tabular-nums">
            step {idx + 1} of {total}
          </div>
        </div>
      </div>
    </div>
  );
}

function Outro({ claim }) {
  const flaggedCount = claim.steps.filter((s) => s.verdict === 'flag').length;
  const passedCount = claim.steps.length - flaggedCount;
  return (
    <div className="text-center py-6 animate-[fadeIn_0.4s_ease-out_both]">
      <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-400/50 grid place-items-center mx-auto mb-4">
        <AlertTriangle className="w-8 h-8 text-amber-300" strokeWidth={2.5} />
      </div>
      <div className="text-2xl font-bold text-white mb-2">
        {flaggedCount} of {claim.steps.length} checks flagged before human review.
      </div>
      <div className="text-base text-slate-300 max-w-xl mx-auto leading-relaxed">
        {claim.division} received a coaching packet the same minute the claim was submitted. Re-upload → re-screen. Only after all 5 clear does the claim reach a human reviewer — with an AI-graded summary attached.
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4" /> {flaggedCount} gap{flaggedCount !== 1 ? 's' : ''} caught
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-sm font-semibold">
          <Check className="w-4 h-4" strokeWidth={3} /> {passedCount} passed
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-400/40 text-orange-300 text-sm font-semibold">
          {claim.docsProcessed} documents AI-graded · {claim.aiTime}
        </div>
      </div>
    </div>
  );
}
